// src/pages/HrOverviewPage.js
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCheck, ClipboardList, Handshake, Bell, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// vorkonfigurierte Clients
import { mentoringApi as axios, authApi } from '../axiosInstance';
import { ensureMentoringSession } from '../lib/session';

import MenteeSatisfactionChart from '../components/MenteeSatisfactionChart';
import MentorSatisfactionChart from '../components/MentorSatisfactionChart';
import MenteeProgressChart from '../components/MenteeProgressChart';
import MenteeEngagementHeatmap from '../components/MenteeEngagementHeatmap';
import ChatPopup from '../components/ChatPopup';

/* ============================== Helper ============================== */

const DEV = process.env.NODE_ENV !== 'production';

/**
 * Entfernt nur doppelte führende Slashes und einen evtl. doppelt
 * vorhandenen "/mentoring" Präfix im Pfad-Argument.
 * (Axios-Basis-URL kann /mentoring bereits enthalten.)
 */
function mPath(p = '') {
  const s = String(p || '');
  return s.replace(/^\/+/, '/').replace(/^\/mentoring(\/|$)/i, '/');
}

async function tryGet(client, url, timeoutMs = 4000) {
  try {
    const r = await client.get(mPath(url), { withCredentials: true, timeout: timeoutMs });
    return r?.data ?? null;
  } catch {
    return null;
  }
}

function normalizeProfile(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const src = raw.user ?? raw.profile ?? raw.hr_profile ?? raw.data ?? raw;

  const customer = src.customer ?? src.tenant ?? {};
  const subscription = src.subscription ?? src.plan_info ?? {};

  // <-- WICHTIG: jetzt auch `firstname` / `lastname` unterstützt
  const first = src.first_name ?? src.firstName ?? src.firstname ?? src.given_name ?? '';
  const last = src.last_name ?? src.lastName ?? src.lastname ?? src.family_name ?? '';

  const role = src.role ?? src.user_role ?? raw.role ?? '';
  const customer_id = src.customer_id ?? src.customerId ?? customer.id ?? src.tenant_id ?? '';

  const pkg =
    src.package ??
    src.package_name ??
    src.plan ??
    src.plan_name ??
    subscription.package ??
    subscription.plan ??
    '';

  return {
    first_name: first,
    last_name: last,
    role,
    customer_id,
    package: pkg,
  };
}

function normalizePackageFromAny(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const src = raw.data ?? raw;
  const user = src.user ?? src.profile ?? src.hr_profile ?? {};
  const subscription = src.subscription ?? src.plan_info ?? src.current_subscription ?? {};
  const license = src.license ?? src.licenses ?? {};
  const product = src.product ?? {};
  const candidates = [
    src.package,
    src.package_name,
    src.plan,
    src.plan_name,
    src.tier,
    src.currentPackage,
    src.current_plan,
    user.package,
    user.package_name,
    user.plan,
    user.plan_name,
    subscription.package,
    subscription.plan,
    subscription.plan_name,
    subscription.tier,
    license.package,
    license.plan,
    license.plan_name,
    product.name,
    product.plan,
    product.package,
  ];
  for (const c of candidates) if (typeof c === 'string' && c.trim()) return c.trim();
  return '';
}

function toSafePositiveInt(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/* ---------- Persistenter, simpler Profil-Cache ---------- */
const PROFILE_CACHE_KEY = 'hr_profile_cache_v1';
const readCachedProfile = () => {
  try {
    return JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY) || 'null');
  } catch {
    return null;
  }
};
const writeCachedProfile = (p) => {
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(p || {}));
  } catch {}
};

/* ---------- NEU: Lokaler Paket-Fallback ---------- */
function readLocalPackageFallback() {
  try {
    const keys = ['hr_invite_package', 'currentPackage', 'lastPackage'];
    for (const k of keys) {
      const v = localStorage.getItem(k);
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  } catch {}
  return '';
}

/* ---------- Projektfilter (globaler HR-Filter) ---------- */
const PROJECT_FILTER_KEY = 'hr_overview_project_filter_v1';
function readSavedProjectFilter() {
  try {
    return localStorage.getItem(PROJECT_FILTER_KEY) || '';
  } catch {
    return '';
  }
}
function saveProjectFilter(v) {
  try {
    if (v) localStorage.setItem(PROJECT_FILTER_KEY, String(v));
    else localStorage.removeItem(PROJECT_FILTER_KEY);
  } catch {}
}

/* ============================== Component ============================== */

export default function HrOverviewPage() {
  const [metrics, setMetrics] = useState(null);
  const [profile, setProfile] = useState(() => readCachedProfile());
  const [tenantPkg, setTenantPkg] = useState(''); // Paket aus /mentoring/tenant-package
  const [readySummary, setReadySummary] = useState({ mentees: 0, mentors: 0 });

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Projekt-Dropdown (wirkt auf Dashboard + wird als globaler HR-Filter persistiert)
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => readSavedProjectFilter()); // '' = Alle

  // HR-Chat
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadList, setUnreadList] = useState([]);
  const [showUnreadPopup, setShowUnreadPopup] = useState(false);

  // chatContext: { menteeId, mentorId, senderRole, receiverRole, threadType, presetMessage, title }
  const [chatContext, setChatContext] = useState(null);
  const [showChatPopup, setShowChatPopup] = useState(false);

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const navigateTo = (path) => () => navigate(path);

  function handleNewSurvey() {
    navigate('/hr/new-survey');
  }

  /* ---------- Lokale Helper (Badge sofort aktualisieren) ---------- */

  async function refreshUnread() {
    try {
      const res = await axios.get(mPath('/chat/unread/hr'), {
        withCredentials: true,
        timeout: 4000,
      });
      const list = Array.isArray(res?.data) ? res.data : [];
      setUnreadCount(list.length);
      setUnreadList(list);
    } catch (e) {
      if (DEV) console.warn('[HR] refreshUnread failed:', e?.message);
    }
  }

  async function markThreadReadForHr(menteeId, mentorId) {
    try {
      await axios.patch(
        mPath('/chat/messages/mark-as-read'),
        { menteeId: menteeId ?? null, mentorId: mentorId ?? null, role: 'hr' },
        { withCredentials: true, timeout: 4000 }
      );
    } catch (e) {
      if (DEV) console.warn('[HR] mark-as-read failed:', e?.message);
    }
  }

  /* --------------------------- Daten laden --------------------------- */
  useEffect(() => {
    let alive = true;

    async function fetchData() {
      setLoading(true);
      setLoadError('');
      try {
        // 0) sicherstellen, dass die Mentoring-Session „angeheftet“ ist
        await ensureMentoringSession();

        // Aktiver Projektfilter (optional)
        const safeSurveyId = toSafePositiveInt(selectedProjectId);

        // 1) Profil (Fallback-Reihenfolge: /auth/profile -> /auth/whoami -> /mentoring/whoami)
        let profileRaw = await tryGet(authApi, '/profile', 5000);
        if (!profileRaw?.user) profileRaw = await tryGet(authApi, '/whoami', 5000);
        if (!profileRaw?.user) profileRaw = await tryGet(axios, '/whoami', 5000);

        let normalized = normalizeProfile(profileRaw || {});

        // Cache mergen/setzen
        const cached = readCachedProfile() || {};
        normalized = { ...cached, ...normalized };
        if (!alive) return;
        setProfile(normalized);
        writeCachedProfile(normalized);

        const safeCustomerId = toSafePositiveInt(normalized.customer_id);

        // 1a) Paket aus /mentoring/tenant-package laden
        const tp = await tryGet(axios, '/tenant-package', 5000);
        const pkgFromApi = normalizePackageFromAny(tp);
        if (pkgFromApi) {
          if (!alive) return;
          setTenantPkg(pkgFromApi);
          const merged = { ...normalized, package: normalized.package || pkgFromApi };
          setProfile(merged);
          writeCachedProfile(merged);
          try {
            localStorage.setItem('lastPackage', pkgFromApi);
          } catch {}
        }

        // 2) Kennzahlen laden (jetzt optional pro Survey)
        const qCustomer = safeCustomerId ? `customer_id=${safeCustomerId}` : '';
        const qSurvey = safeSurveyId ? `survey_id=${safeSurveyId}` : '';
        const qs = [qCustomer, qSurvey].filter(Boolean).join('&');
        const metricsUrl = '/overview-metrics' + (qs ? `?${qs}` : '');

        const [overview, projectsCount] = await Promise.all([
          tryGet(axios, metricsUrl, 8000),
          tryGet(axios, '/projects-count' + (safeCustomerId ? `?customer_id=${safeCustomerId}` : ''), 6000),
        ]);

        const menteesReady =
          Number(overview?.mentees_total ?? overview?.mentees_count ?? overview?.mentees ?? 0) || 0;
        const mentorsReady =
          Number(overview?.mentors_total ?? overview?.mentors_count ?? overview?.mentors ?? 0) || 0;

        const pairsConfirmed =
          Number(
            overview?.confirmed_pairs_total ??
              overview?.confirmed_pairs_count ??
              overview?.confirmed_pairs ??
              overview?.confirmedPairs ??
              0
          ) || 0;

        const projTotal =
          Number(projectsCount?.total ?? projectsCount?.count ?? projectsCount?.data?.total ?? 0) || 0;

        // Wenn ein Projekt ausgewählt ist, zeigt die Kachel logisch 1 Projekt
        const surveyOrProjectCount = safeSurveyId ? 1 : projTotal || Number(overview?.survey_count ?? 0) || 0;

        if (!alive) return;
        setReadySummary({ mentees: menteesReady, mentors: mentorsReady });
        setMetrics({
          survey_count: surveyOrProjectCount,
          confirmed_pairs_count: pairsConfirmed,
        });

        setLoading(false);
      } catch (err) {
        if (DEV) console.error('HR Overview – fetchData fatal:', err);
        if (!alive) return;
        setMetrics({ survey_count: 0, confirmed_pairs_count: 0 });
        setReadySummary({ mentees: 0, mentors: 0 });
        setLoadError(t('errorLoading', 'Fehler beim Laden.'));
        setLoading(false);
      }
    }

    fetchData();
    return () => {
      alive = false;
    };
  }, [t, selectedProjectId]); // <-- wichtig: neu laden bei Filterwechsel

  /* ----------------------- Projekte für Dropdown ---------------------- */
  useEffect(() => {
    let alive = true;

    async function loadProjects() {
      setProjectsLoading(true);
      try {
        // bewusst fetch: sicherer gegen BaseURL-Verwechslungen
        const r = await fetch('/survey/surveys', { credentials: 'include' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json().catch(() => []);
        const list = Array.isArray(data) ? data : [];
        if (!alive) return;
        setProjects(list);
      } catch (e) {
        if (DEV) console.warn('[HR] loadProjects failed:', e?.message);
        if (!alive) return;
        setProjects([]);
      } finally {
        if (alive) setProjectsLoading(false);
      }
    }

    loadProjects();
    return () => {
      alive = false;
    };
  }, []);

  /* ---------------------- Ungelesene Nachrichten --------------------- */
  useEffect(() => {
    let stop = false;
    const run = async () => {
      if (!stop) await refreshUnread();
    };
    run(); // initial
    const id = setInterval(run, 10_000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* -------- Öffnen via externem Event (falls anderswo verwendet) ---- */
  useEffect(() => {
    const toInt = (v) => {
      const n = Number(v);
      return Number.isInteger(n) && n > 0 ? n : null;
    };
    const handleChatOpen = async (e) => {
      const { menteeId, mentorId, senderRole, receiverRole, presetMessage, threadType } = e.detail || {};
      if (DEV)
        console.debug('[HR] openChatPopup (raw)', {
          menteeId,
          mentorId,
          senderRole,
          receiverRole,
          presetMessage,
          threadType,
        });

      // HR-Thread: menteeId ODER mentorId ist zulässig
      if (threadType === 'hr') {
        const midMentee = toInt(menteeId);
        const midMentor = toInt(mentorId);
        if (!midMentee && !midMentor) return;

        const rcv = receiverRole || (midMentee ? 'mentee' : 'mentor');

        let title = '';
        if (midMentee) {
          const res = await tryGet(axios, `/chat/user-name?menteeId=${midMentee}`, 2500);
          const n = res?.name;
          title = n ? `Chat mit Mentee ${n}` : `Chat mit Mentee #${midMentee}`;
        } else if (midMentor) {
          const res = await tryGet(axios, `/chat/user-name?mentorId=${midMentor}`, 2500);
          const n = res?.name;
          title = n ? `Chat mit Mentor ${n}` : `Chat mit Mentor #${midMentor}`;
        }

        setChatContext({
          menteeId: midMentee ?? null,
          mentorId: midMentor ?? null,
          senderRole: senderRole || 'hr',
          receiverRole: rcv,
          threadType: 'hr',
          presetMessage: presetMessage || '',
          title,
        });
        setShowChatPopup(true);
        return;
      }

      // klassischer Pair-Thread nur bei gültigen IDs
      const norm = { menteeId: toInt(menteeId), mentorId: toInt(mentorId) };
      if (!norm.menteeId || !norm.mentorId) return;

      const rcv = receiverRole || 'mentee';
      const title = rcv === 'mentee' ? `Chat mit Mentee #${norm.menteeId}` : `Chat mit Mentor #${norm.mentorId}`;

      setChatContext({
        ...norm,
        senderRole: senderRole || 'hr',
        receiverRole: rcv,
        threadType: 'pair',
        presetMessage: presetMessage || '',
        title,
      });
      setShowChatPopup(true);
    };
    window.addEventListener('openChatPopup', handleChatOpen);
    return () => window.removeEventListener('openChatPopup', handleChatOpen);
  }, []);

  /* --------------------- Lokales Öffnen aus Liste -------------------- */
  async function openFromUnread(msg) {
    const menteeId = Number(msg.mentee_id) || null;
    const mentorId = Number(msg.mentor_id) || null;

    const sender = String(msg.sender_role || '').toLowerCase();
    const isFromMentee = sender === 'mentee';
    const isFromMentor = sender === 'mentor';

    // 1) Als gelesen markieren – gezielt pro Kanal
    if (isFromMentee) {
      await markThreadReadForHr(menteeId, null);
    } else if (isFromMentor) {
      await markThreadReadForHr(null, mentorId);
    } else {
      await markThreadReadForHr(menteeId, mentorId);
    }
    refreshUnread();

    // 2) Titel
    const title =
      isFromMentee && menteeId
        ? `Chat mit Mentee #${menteeId}`
        : isFromMentor && mentorId
        ? `Chat mit Mentor #${mentorId}`
        : t('chat', 'Chat');

    // 3) Chatkontext
    if (isFromMentee) {
      setChatContext({
        menteeId,
        mentorId: null,
        senderRole: 'hr',
        receiverRole: 'mentee',
        threadType: 'hr',
        presetMessage: '',
        title,
      });
    } else {
      setChatContext({
        menteeId: null,
        mentorId,
        senderRole: 'hr',
        receiverRole: 'mentor',
        threadType: 'hr',
        presetMessage: '',
        title,
      });
    }

    setShowChatPopup(true);
    setShowUnreadPopup(false);
  }

  /* ----------------------------- Render ------------------------------ */

  if (loading) return <p className="p-6 text-white">{t('loadingMetrics', 'Lade Metriken.')}</p>;

  if (loadError && !metrics) {
    return (
      <div className="min-h-screen bg-black text-white p-6">
        <p className="text-red-400 mb-4">{loadError}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-mentixxgreen text-black font-semibold px-4 py-2 rounded-2xl"
        >
          {t('reload', 'Neu laden')}
        </button>
      </div>
    );
  }

  const activeSurveyId = selectedProjectId ? String(selectedProjectId) : '';
  const surveyQuery = activeSurveyId ? `?surveyId=${encodeURIComponent(activeSurveyId)}` : '';

  const cards = [
    {
      title: t('menteesTotal', 'Mentees (gesamt)'),
      value: readySummary.mentees,
      icon: <Users size={28} className="text-green-400" />,
      onClick: navigateTo(`/hr/mentees${surveyQuery}`),
    },
    {
      title: t('mentorsTotal', 'Mentoren (gesamt)'),
      value: readySummary.mentors,
      icon: <UserCheck size={28} className="text-green-400" />,
      onClick: navigateTo(`/hr/mentors${surveyQuery}`),
    },
    {
      title: t('surveysProjects', 'Umfragen / Projekte'),
      value: metrics?.survey_count ?? 0,
      icon: <ClipboardList size={28} className="text-green-400" />,
      onClick: navigateTo('/hr'),
    },
    {
      title: t('confirmedPairs', 'Bestätigte Paare'),
      value: metrics?.confirmed_pairs_count ?? 0,
      icon: <Handshake size={28} className="text-green-400" />,
      onClick: navigateTo(`/hr/confirmed-pairs${surveyQuery}`),
    },
  ];

  const isValidChat =
    !!chatContext &&
    typeof chatContext.senderRole === 'string' &&
    typeof chatContext.receiverRole === 'string' &&
    ((chatContext.threadType === 'hr' &&
      (Number.isInteger(chatContext.menteeId) || Number.isInteger(chatContext.mentorId))) ||
      (chatContext.threadType !== 'hr' &&
        Number.isInteger(chatContext.menteeId) &&
        Number.isInteger(chatContext.mentorId)));

  // zusätzlicher Fallback aus localStorage, falls API/Profil kein Paket liefern
  const pkgDisplay = tenantPkg || profile?.package || readLocalPackageFallback() || '';

  // ✅ NEU: hr_user Flag für UI-Sperren
  const isHrUser = String(profile?.role || '').toLowerCase() === 'hr_user';

  // Chart-Filter (für Charts + Widgets)
  const chartSurveyId = selectedProjectId ? Number(selectedProjectId) : null;
  const chartKey = chartSurveyId ? `s${chartSurveyId}` : 'all';

  return (
    <div className="min-h-screen bg-black text-white p-6 space-y-6 relative">
      {/* Benachrichtigungen */}
      <div className="absolute top-4 right-4 z-50">
        <button onClick={() => setShowUnreadPopup(!showUnreadPopup)} className="relative">
          <Bell className="text-gray-300 w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </button>

        {showUnreadPopup && (
          <div className="absolute right-0 mt-2 w-80 bg-white border rounded-lg shadow-lg p-3 z-50 text-black">
            <h4 className="font-bold text-sm mb-2">{t('openChats', 'Offene Chats')}</h4>
            {unreadList.length === 0 ? (
              <p className="text-sm text-gray-500">{t('noUnreadMessages', 'Keine ungelesenen Nachrichten')}</p>
            ) : (
              <ul className="text-sm space-y-1 max-h-80 overflow-y-auto">
                {unreadList.map((msg, idx) => (
                  <li key={idx} className="border-b pb-1 mb-1">
                    <strong>{msg.sender_role === 'mentee' ? t('mentee', 'Mentee') : t('mentor', 'Mentor')}</strong>:{' '}
                    {msg.message}
                    <br />
                    <span className="text-xs text-gray-500">{new Date(msg.timestamp).toLocaleString()}</span>
                    <br />
                    <button className="text-blue-600 text-xs mt-1" onClick={() => openFromUnread(msg)}>
                      {t('openChat', 'Chat öffnen')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* HR-Profil Button */}
      <button
        onClick={() => navigate('/hr-profile')}
        className="bg-green-400 text-black font-semibold px-4 py-2 rounded-full hover:bg-green-300 transition"
      >
        {t('toHrProfile', 'Zum HR-Profil')}
      </button>

      {/* Überschrift + Neues Projekt */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">{t('hrDashboard', 'HR-Dashboard')}</h1>

        {!isHrUser && (
          <button
            onClick={handleNewSurvey}
            className="flex items-center gap-2 bg-mentixxgreen text-black font-semibold px-4 py-2 rounded-2xl hover:bg-green-400 transition"
          >
            <Plus className="w-4 h-4" />
            {t('newProject', 'Neues Projekt')}
          </button>
        )}
      </div>

      {/* Begrüßung / Paket-Info */}
      <div className="bg-gray-700 text-white rounded-2xl p-6 shadow-md">
        <p className="text-lg">
          {t('welcomeUser', {
            firstName: profile?.first_name || 'HR',
            lastName: profile?.last_name || '',
            role: profile?.role,
          })}
        </p>
        <p className="text-sm text-gray-300">
          {t('customerId', 'Kunden-ID')}: {profile?.customer_id ?? '–'}
        </p>

        {pkgDisplay && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-800 text-sm">
              {t('bookedPackage', 'Gebuchtes Paket')}: <strong className="ml-2">{pkgDisplay}</strong>
            </span>

            {!isHrUser && (
              <button
                onClick={() =>
                  navigate(
                    `/hr/upgrade?lang=${encodeURIComponent(i18n?.language || 'de')}&current=${encodeURIComponent(
                      pkgDisplay
                    )}`
                  )
                }
                className="px-3 py-1 rounded-full bg-mentixxgreen text-black font-semibold hover:bg-green-300 transition text-sm"
              >
                {t('upgradePackage', 'Paket upgraden')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ✅ Projekt-Dropdown (globaler Filter) */}
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <span className="text-sm text-gray-300">{t('project', 'Projekt')}:</span>

        <div className="flex items-center gap-2">
          <select
            value={selectedProjectId}
            onChange={(e) => {
              const v = e.target.value || '';
              setSelectedProjectId(v);
              saveProjectFilter(v);
            }}
            className="bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">{t('allProjects', 'Alle Projekte')}</option>
            {projects.map((p) => {
              const id = p?.survey_id ?? p?.id ?? p?.project_id ?? p?.surveyId ?? p?.projectId;
              const title = p?.title ?? p?.name ?? `#${id}`;
              if (!id) return null;
              return (
                <option key={String(id)} value={String(id)}>
                  {title}
                </option>
              );
            })}
          </select>

          {projectsLoading && (
            <span className="text-xs text-gray-400">{t('loadingProjects', 'Lade Projekte...')}</span>
          )}
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => (
          <div
            key={index}
            onClick={card.onClick}
            className={`bg-gray-800 rounded-2xl p-6 shadow-md hover:shadow-xl transition duration-200 ${
              card.onClick ? 'cursor-pointer hover:bg-gray-700' : ''
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-gray-400">{card.title}</div>
              {card.icon}
            </div>
            <div className="text-4xl font-semibold">{card.value}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
        <MenteeSatisfactionChart key={`ms-${chartKey}`} surveyId={chartSurveyId} />
        <MentorSatisfactionChart key={`mos-${chartKey}`} surveyId={chartSurveyId} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 items-stretch">
        <div className="h-[480px]">
          <MenteeProgressChart key={`mp-${chartKey}`} surveyId={chartSurveyId} />
        </div>
        <div className="h-[480px] overflow-y-auto">
          <MenteeEngagementHeatmap key={`meh-${chartKey}`} surveyId={chartSurveyId} />
        </div>
      </div>

      {/* Chat Popup */}
      {showChatPopup && isValidChat && (
        <ChatPopup
          menteeId={chatContext.menteeId}
          mentorId={chatContext.mentorId}
          senderRole={chatContext.senderRole}
          receiverRole={chatContext.receiverRole}
          threadType={chatContext.threadType || 'pair'}
          presetMessage={chatContext.presetMessage}
          title={chatContext.title}
          onClose={() => {
            setShowChatPopup(false);
            setChatContext(null);
          }}
        />
      )}
    </div>
  );
}
